/**
Copyright (C) 2019-2022, Infraclear Inc. All Rights Reserved.
*/

import React, { useEffect, useState } from "react";
import "./forgotPassword.scss";
import { ErrorCodes } from "../../constants/messageCodes";
import { MessageText } from "../../constants/messageText";
import CloseIcon from "@mui/icons-material/Close";
import { passwordLength } from "../../constants/env-url";
import { Button, TextField, IconButton, CircularProgress } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import instance from "../../utils/axiosInstance";
import { SnackBarMessage } from "../snackBar/snackBar";
import {
  ForgotPasswordAPI,
  ForgotPasswordCideAPI,
} from "../../constants/api-urlConstants";
import { PasswordFormat } from "../passwordFormat/passwordFormat";
import { encryptpassword } from "../../constants/commonConstants";
import { whiteSpaceRestrict } from "../../constants/commonConstants";

export const ForgotPassword: React.FC<any> = ({
  passChangeSuccessfully,
  forgotPassClose,
  emailIDValue,
}) => {
  const [msgConfig, setMsgConfig] = useState({
    type: "",
    message: "",
    snackFlag: false,
  });

  const [showResetFields, setShowResetFields] = useState<any>(false);
  const [emailVerified, setEmailVerified] = useState<any>(false);
  const [showNewPassword, setShowNewPassword] = useState<any>(false);
  const [showCnfPassword, setShowCnfPassword] = useState<any>(false);
  const [statusIsLoading, setStatusIsLoading] = React.useState(false);
  const [passwordTooltip, setPasswordTooltip] = useState<any>(false);
  const [passwordTextBoxValue, setPasswordTextBoxValue] = useState<any>();
  const [initial_state, setInitial_state] = useState<any>({
    email: emailIDValue,
    code: "",
    newPassword: "",
    confirmpassword: "",
  });
  const [initial_state_email, setInitial_state_email] = useState<any>({
    email: emailIDValue,
  });
  const regex = new RegExp(
    `^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{${passwordLength},}$`
  );

  const ForgotPassVAlidation = Yup.object().shape({
    email: Yup.string()
      .email(MessageText.VALIDATION_EMAIL_FORMAT)
      .required(MessageText.VALIDATION_EMAIL_REQUIRED)
      .typeError(MessageText.VALIDATION_EMAIL_REQUIRED),
  });

  const ChangePasswordValidation = Yup.object().shape({
    email: Yup.string()
      .email(MessageText.VALIDATION_EMAIL_FORMAT)
      .required(MessageText.VALIDATION_EMAIL_REQUIRED)
      .typeError(MessageText.VALIDATION_EMAIL_REQUIRED),
    code: Yup.string().required(MessageText.RESET_CODE_REQUIRED),
    newPassword: Yup.string().required(MessageText.VALIDATION_PASS_REQUIRED).matches(regex, MessageText.VALIDATION_PASS_INVALID),
    confirmpassword: Yup.string().required(MessageText.VALIDATION_CONFIRM_PASS_REQUIRED).oneOf([Yup.ref("newPassword")], MessageText.VALIDATION_PASS_MATCH),
  });

  useEffect(() => { }, []);

  const onFinish = (credentials: any) => {
    setStatusIsLoading(true);
    if (credentials) {
      !showResetFields
        ? sendRestPasswordCode(credentials)
        : resetPassword(credentials);
    } else {
    }
  };

  const sendRestPasswordCode = (credentials: any) => {
    const USER_API_BASE_URL = `${ForgotPasswordAPI}`;
    const userData = {
      email: credentials.email,
    };
    instance
      .post(USER_API_BASE_URL, userData)
      .then((response: any) => {
        setStatusIsLoading(false);
        if (response.data.success) {
          setMsgConfig({
            type: "info",
            message: MessageText.RESET_CODE_SENT,
            snackFlag: true,
          });
          setShowResetFields(true);
          setInitial_state(
            {
              email: credentials.email,
              code: "",
              newPassword: "",
              confirmpassword: ""
            }
          )
          setInitial_state_email({ email: credentials.email })
          setEmailVerified(true);
        } else if (
          response.data.message_code === ErrorCodes.LIMIT_EXCEED_FORGOT
        ) {
          setMsgConfig({
            type: "error",
            message: MessageText.LIMIT_EXCEED_PASSWORD,
            snackFlag: true,
          });
        }
        else if (
          response.data.message_code === ErrorCodes.INVALID_USER_STATUS_FOR_PWD_RESET
        ) {
          setMsgConfig({
            type: "error",
            message: MessageText.RESET_TRIAL_USER_HELP_MESSAGE,
            snackFlag: true,
          });
        }
        else {
          setMsgConfig({
            type: "error",
            message: MessageText.WENT_WRONG,
            snackFlag: true,
          });
        }
      })
      .catch((error) => {
        setStatusIsLoading(false);
      });
  };

  const resetPassword = async (credentials: any) => {
    const USER_API_BASE_URL = `${ForgotPasswordCideAPI}`;
    const encrypt_password: any = await encryptpassword(credentials.newPassword);
    const userData = {
      email: credentials.email,
      password: encrypt_password,
      code: credentials.code,
    };
    instance
      .post(USER_API_BASE_URL, userData)
      .then((response: any) => {
        setStatusIsLoading(false);
        if (response.data.success) {
          passChangeSuccessfully(true);
        } else if (
          response.data.message_code === ErrorCodes.INVALID_MFA_CODE ||
          response.data.message_code === ErrorCodes.INVALID_CODE_FORGOT
        ) {
          setMsgConfig({
            type: "error",
            message: MessageText.RESET_PASSWORD_CODE,
            snackFlag: true,
          });
        } else if (
          response.data.message_code === ErrorCodes.LIMIT_EXCEED_FORGOT
        ) {
          setMsgConfig({
            type: "error",
            message: MessageText.LIMIT_EXCEED_PASSWORD,
            snackFlag: true,
          });
        }
      })
      .catch((error) => {
        setStatusIsLoading(false);
      });
  };

  const goToLogin = () => {
    if (showResetFields) {
      setShowResetFields(false);
    } else {
      forgotPassClose();
    }
  };

  return (
    <div className="forgotPasswordWrapper">
      <div className="forgotPasswordWrapper">
        <div className="headerWrapper">
          <span className="changePasswordtitle">Forgot password</span>
          <IconButton
            className="closePosition"
            aria-label="close"
            onClick={goToLogin}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        {!showResetFields &&
          <Formik
            enableReinitialize={true}
            initialValues={initial_state_email}
            validationSchema={ForgotPassVAlidation}
            onSubmit={onFinish}
          >
            {({
              touched,
              errors,
              values,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Field
                  as={TextField}
                  margin="normal"
                  error={touched.email && Boolean(errors.email)}
                  fullWidth
                  label="Email*"
                  name="email"
                  autoComplete="email"
                  size="small"
                  disabled={emailVerified}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    autoComplete: "new-email",
                    form: {
                      autoComplete: "off",
                    },
                  }}
                  helperText={<ErrorMessage name="email" />}
                />
                <div className="loader">
                  {statusIsLoading && (
                    <div>
                      <CircularProgress size={25} />
                    </div>
                  )}
                  {!statusIsLoading && (
                    <Button
                      type="submit"
                      variant="contained"
                      className="submitBtn"
                      style={{ textTransform: "none" }}
                    >
                      Send Reset Code
                    </Button>
                  )}
                </div>
                {!showResetFields && (
                  <div className="haveCode">
                    <span onClick={() => setShowResetFields(true)}>
                      Already have a reset code?
                    </span>
                  </div>
                )}
              </Form>
            )}
          </Formik>
        }
        {showResetFields &&
          <Formik
            enableReinitialize={true}
            initialValues={initial_state}
            validationSchema={ChangePasswordValidation}
            onSubmit={onFinish}
          >
            {({
              touched,
              errors,
              values,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Field
                  as={TextField}
                  margin="normal"
                  fullWidth
                  label="Email*"
                  name="email"
                  autoComplete="email"
                  size="small"
                  disabled={emailVerified}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    autoComplete: "new-email",
                    form: {
                      autoComplete: "off",
                    },
                  }}
                  error={touched.email && Boolean(errors.email)}
                  helperText={<ErrorMessage name="email" />}
                />
                <div>
                  <Field
                    as={TextField}
                    margin="normal"
                    error={touched.code && Boolean(errors.code)}
                    fullWidth
                    label="Verification Code*"
                    name="code"
                    autoComplete="code"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      autoComplete: "new-email",
                      form: {
                        autoComplete: "off",
                      },
                    }}
                    helperText={<ErrorMessage name="code" />}
                  />
                  <div className="newPassword">
                    {passwordTooltip && (
                      <PasswordFormat
                        textBoxValue={passwordTextBoxValue}
                        allTrue={(value: any) => { }}
                      />
                    )}
                    <Field
                      as={TextField}
                      type={showNewPassword ? "text" : "password"}
                      margin="normal"
                      fullWidth
                      name="newPassword"
                      label="New Password*"
                      id="newPassword"
                      onKeyPress={(e: any) => whiteSpaceRestrict(e)}
                      onFocus={(event: any) => {
                        setPasswordTextBoxValue(event.target.value);
                        setPasswordTooltip(true);
                      }}
                      onBlur={(event: any) => {
                        setPasswordTooltip(false);
                        handleBlur(event);
                      }}
                      onChange={(event: any) => {
                        setFieldValue("newPassword", event.target.value);
                        setPasswordTextBoxValue(event.target.value);
                      }}
                      error={touched.newPassword && Boolean(errors.newPassword)}
                      helperText={<ErrorMessage name="newPassword" />}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton tabIndex={-1}
                              aria-label="toggle password visibility"
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                            >
                              {showNewPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                  <Field
                    as={TextField}
                    margin="normal"
                    fullWidth
                    name="confirmpassword"
                    label="Confirm New Password*"
                    type={showCnfPassword ? "text" : "password"}
                    id="confirmpassword"
                    size="small"
                    error={
                      touched.confirmpassword && Boolean(errors.confirmpassword)
                    }
                    helperText={<ErrorMessage name="confirmpassword" />}
                    InputLabelProps={{ shrink: true }}
                    onKeyPress={(e: any) => whiteSpaceRestrict(e)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton tabIndex={-1}
                            aria-label="toggle password visibility"
                            onClick={() => setShowCnfPassword(!showCnfPassword)}
                          >
                            {showCnfPassword ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <div className="loader">
                  {statusIsLoading && (
                    <div>
                      <CircularProgress size={25} />
                    </div>
                  )}
                  {!statusIsLoading && (
                    <Button
                      type="submit"
                      variant="contained"
                      className="submitBtn"
                      style={{ textTransform: "none" }}
                    >
                      Reset Password
                    </Button>
                  )}
                </div>
                {!showResetFields && (
                  <div className="haveCode">
                    <span onClick={() => setShowResetFields(true)}>
                      Already have a reset code?
                    </span>
                  </div>
                )}
              </Form>
            )}
          </Formik>
        }
      </div>
      {msgConfig.snackFlag && (
        <div>
          <SnackBarMessage snacBarValue={msgConfig} />
        </div>
      )}
    </div>
  );
};
